import axios from 'axios';

import {
    trackEditRequestCancelled,
    trackEditRequestEdited,
    trackEditRequestProposed,
    trackEditRequestWithdrawn,
    trackHamiltonLimitReached,
    trackHamiltonMessageSent,
} from '@/posthog_tracking';
import { PROPOSAL_PENDING, PROPOSAL_RESULTS, transcriptItems, turnFailure } from '@/services/hamilton';
import { API_ROOT, logApiError } from '@/store/api';

const TITLE_MAX_LENGTH = 100;

function conversationsUrl(rootGetters, suffix = '') {
    return `${API_ROOT}/${rootGetters.user.uuid}/conversations/${suffix}`;
}

function editRequestsUrl(rootGetters, intake, suffix = '') {
    return `${API_ROOT}/${rootGetters.user.uuid}/onboardings/${intake}/edit-requests/${suffix}`;
}

const STALE_EDIT_REQUEST_STATUSES = [404, 409];

function intakeProduct(rootGetters, intake) {
    if (!intake) return null;
    const onboarding = (rootGetters['onboarding/onboardings'] || []).find(item => item.uuid === intake);
    return onboarding?.selected_product?.product_label || null;
}

function withoutResolvedDrafts(drafts, rows) {
    const resolved = rows
        .filter(row => row.tool_call_id && (row.role === 'tool' || (row.role === 'assistant' && !row.is_pending)))
        .map(row => row.tool_call_id)
        .filter(toolCallId => Object.hasOwn(drafts, toolCallId));
    if (!resolved.length) return drafts;
    return Object.fromEntries(Object.entries(drafts).filter(([toolCallId]) => !resolved.includes(toolCallId)));
}

function initialState() {
    return {
        isOpen: false,
        openTab: null,
        intake: null,
        conversations: [],
        conversationsStatus: 'idle',
        selectedId: null,
        messages: {},
        appendedCounts: {},
        transcriptStatus: 'idle',
        sendingContent: null,
        resolvingCallId: null,
        proposalDrafts: {},
        editRequestsRevision: 0,
        editRequests: [],
        editRequestsIntake: null,
        editRequestsLoadId: 0,
        editRequestsStatus: 'idle',
        limit: null,
    };
}

export default {
    namespaced: true,
    state: initialState,
    mutations: {
        reset(state) {
            Object.assign(state, initialState());
        },
        setOpen(state, isOpen) {
            state.isOpen = isOpen;
        },
        setOpenTab(state, tab) {
            state.openTab = tab;
        },
        setIntake(state, intake) {
            state.intake = intake;
        },
        setConversations(state, conversations) {
            state.conversations = conversations;
        },
        clearConversations(state) {
            state.intake = undefined;
            state.conversations = [];
            state.conversationsStatus = 'idle';
            state.selectedId = null;
        },
        setConversationsStatus(state, status) {
            state.conversationsStatus = status;
        },
        addConversation(state, conversation) {
            state.conversations = [conversation, ...state.conversations.filter(c => c.id !== conversation.id)];
        },
        removeConversation(state, id) {
            state.conversations = state.conversations.filter(c => c.id !== id);
            const { [id]: _removed, ...messages } = state.messages;
            state.messages = messages;
            if (state.selectedId === id) state.selectedId = null;
            state.proposalDrafts = Object.fromEntries(
                Object.entries(state.proposalDrafts).filter(([, draft]) => draft.conversationId !== id),
            );
        },
        setSelectedId(state, id) {
            state.selectedId = id;
        },
        setMessages(state, { id, rows }) {
            state.messages = { ...state.messages, [id]: rows };
            state.proposalDrafts = withoutResolvedDrafts(state.proposalDrafts, rows);
        },
        appendMessages(state, { id, rows }) {
            const incoming = new Set(rows.map(row => row.sequence));
            const kept = (state.messages[id] || []).filter(row => !incoming.has(row.sequence));
            state.messages = {
                ...state.messages,
                [id]: [...kept, ...rows].sort((a, b) => a.sequence - b.sequence),
            };
            state.appendedCounts = { ...state.appendedCounts, [id]: (state.appendedCounts[id] || 0) + 1 };
            state.proposalDrafts = withoutResolvedDrafts(state.proposalDrafts, rows);
        },
        setTranscriptStatus(state, status) {
            state.transcriptStatus = status;
        },
        setSendingContent(state, content) {
            state.sendingContent = content;
        },
        setResolvingCallId(state, toolCallId) {
            state.resolvingCallId = toolCallId;
        },
        setProposalDraft(state, { toolCallId, text }) {
            state.proposalDrafts = {
                ...state.proposalDrafts,
                [toolCallId]: { conversationId: state.selectedId, text },
            };
        },
        startEditRequestsLoad(state, intake) {
            if (state.editRequestsIntake !== intake) state.editRequests = [];
            state.editRequestsIntake = intake;
            state.editRequestsLoadId += 1;
            state.editRequestsStatus = intake ? 'loading' : 'idle';
        },
        setEditRequests(state, rows) {
            state.editRequests = rows;
            state.editRequestsStatus = 'loaded';
        },
        setEditRequestsStatus(state, status) {
            state.editRequestsStatus = status;
        },
        replaceEditRequest(state, row) {
            state.editRequests = state.editRequests.map(request => (request.uuid === row.uuid ? row : request));
        },
        removeEditRequest(state, uuid) {
            state.editRequests = state.editRequests.filter(request => request.uuid !== uuid);
        },
        bumpEditRequestsRevision(state) {
            state.editRequestsRevision += 1;
        },
        setLimit(state, limit) {
            state.limit = limit;
        },
        clearLimit(state) {
            state.limit = null;
        },
    },
    getters: {
        selectedConversation: state => state.conversations.find(c => c.id === state.selectedId) || null,
        selectedMessages: state => (state.selectedId && state.messages[state.selectedId]) || [],
        isSending: state => state.sendingContent !== null,
        isResolving: state => state.resolvingCallId !== null,
        isLimited: state => state.limit !== null,
    },
    actions: {
        open({ commit }, { tab = null } = {}) {
            commit('setOpenTab', tab && { tab });
            commit('setOpen', true);
        },
        close({ commit }) {
            commit('setOpenTab', null);
            commit('setOpen', false);
        },
        async loadConversations({ commit, dispatch, rootGetters, state }, { intake = null } = {}) {
            if (state.intake !== intake) {
                commit('setSelectedId', null);
                commit('setConversations', []);
            }
            commit('setIntake', intake);
            commit('setConversationsStatus', 'loading');
            const known = new Set(state.conversations.map(c => c.id));
            try {
                const { data } = await axios.get(conversationsUrl(rootGetters), {
                    ...rootGetters.axiosConfig,
                    params: intake ? { intake } : {},
                });
                if (state.intake !== intake) return;
                const listed = new Set(data.map(c => c.id));
                const added = state.conversations.filter(c => !known.has(c.id) && !listed.has(c.id));
                commit('setConversations', [...added, ...data]);
                commit('setConversationsStatus', 'loaded');
            } catch (e) {
                logApiError(e, dispatch);
                if (state.intake === intake) commit('setConversationsStatus', 'error');
            }
        },
        async selectConversation({ commit, dispatch, rootGetters, state }, id) {
            commit('setSelectedId', id);
            if (!id) return;
            commit('setTranscriptStatus', 'loading');
            try {
                let appended;
                let data;
                do {
                    appended = state.appendedCounts[id] || 0;
                    ({ data } = await axios.get(
                        conversationsUrl(rootGetters, `${id}/messages/`),
                        rootGetters.axiosConfig,
                    ));
                } while ((state.appendedCounts[id] || 0) !== appended);
                commit('setMessages', { id, rows: data });
                if (state.selectedId === id) commit('setTranscriptStatus', 'loaded');
            } catch (e) {
                logApiError(e, dispatch);
                if (state.selectedId === id) commit('setTranscriptStatus', 'error');
            }
        },
        startNewConversation({ commit }) {
            commit('setSelectedId', null);
        },
        async sendTurn({ commit, dispatch, rootGetters, state }, { content }) {
            commit('setSendingContent', content);
            const intake = state.intake;
            const product = intakeProduct(rootGetters, intake);
            let id = state.selectedId;
            try {
                if (!id) {
                    const payload = { title: Array.from(content.trim()).slice(0, TITLE_MAX_LENGTH).join('') };
                    if (intake) payload.intake = intake;
                    const { data } = await axios.post(conversationsUrl(rootGetters), payload, rootGetters.axiosConfig);
                    id = data.id;
                    commit('setMessages', { id, rows: [] });
                    if (state.intake === intake) {
                        commit('addConversation', data);
                        commit('setSelectedId', id);
                        commit('setTranscriptStatus', 'loaded');
                    }
                }
                const { data } = await axios.post(
                    conversationsUrl(rootGetters, `${id}/turn/`),
                    { content },
                    rootGetters.axiosConfig,
                );
                commit('appendMessages', { id, rows: data });
                trackHamiltonMessageSent({
                    conversationId: id,
                    intake,
                    product,
                    messageLength: Array.from(content).length,
                });
                for (const item of transcriptItems(data)) {
                    if (item.kind === 'proposal' && item.card.state === PROPOSAL_PENDING) {
                        trackEditRequestProposed({
                            conversationId: id,
                            intake,
                            product,
                            toolCallId: item.card.toolCallId,
                        });
                    }
                }
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                const failure = turnFailure(e);
                if (failure.kind === 'limited') {
                    commit('setLimit', { message: failure.message, until: failure.retryAt });
                    trackHamiltonLimitReached({ conversationId: id, intake, product });
                }
                throw e;
            } finally {
                commit('setSendingContent', null);
            }
        },
        async resolveProposal({ commit, dispatch, rootGetters, state }, { toolCallId, result, text }) {
            const id = state.selectedId;
            commit('setResolvingCallId', toolCallId);
            try {
                const payload = { tool_call_id: toolCallId, result };
                if (result === PROPOSAL_RESULTS.submitted) payload.text = text;
                const { data } = await axios.post(
                    conversationsUrl(rootGetters, `${id}/tool-results/`),
                    payload,
                    rootGetters.axiosConfig,
                );
                if (state.messages[id]) commit('appendMessages', { id, rows: data });
                if (result === PROPOSAL_RESULTS.submitted) commit('bumpEditRequestsRevision');
                if (result === PROPOSAL_RESULTS.cancelled) {
                    trackEditRequestCancelled({
                        conversationId: id,
                        intake: state.intake,
                        product: intakeProduct(rootGetters, state.intake),
                        toolCallId,
                    });
                }
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            } finally {
                commit('setResolvingCallId', null);
            }
        },
        async reconcileProposal({ commit, dispatch, rootGetters, state }, toolCallId) {
            const id = state.selectedId;
            try {
                const { data } = await axios.get(
                    conversationsUrl(rootGetters, `${id}/messages/`),
                    rootGetters.axiosConfig,
                );
                if (state.messages[id]) commit('setMessages', { id, rows: data });
                const proposal = transcriptItems(data).find(
                    item => item.kind === 'proposal' && item.card.toolCallId === toolCallId,
                );
                if (proposal?.card.state === PROPOSAL_RESULTS.submitted) commit('bumpEditRequestsRevision');
                return proposal?.card || null;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async loadEditRequests({ commit, dispatch, rootGetters, state }, intake) {
            commit('startEditRequestsLoad', intake);
            if (!intake) return;
            const loadId = state.editRequestsLoadId;
            try {
                const { data } = await axios.get(editRequestsUrl(rootGetters, intake), rootGetters.axiosConfig);
                if (state.editRequestsLoadId === loadId) commit('setEditRequests', data);
            } catch (e) {
                logApiError(e, dispatch);
                if (state.editRequestsLoadId === loadId) commit('setEditRequestsStatus', 'error');
            }
        },
        async updateEditRequest({ commit, dispatch, rootGetters, state }, { uuid, text }) {
            const intake = state.editRequestsIntake;
            try {
                const { data } = await axios.patch(
                    editRequestsUrl(rootGetters, intake, `${uuid}/`),
                    { text },
                    rootGetters.axiosConfig,
                );
                if (state.editRequestsIntake === intake) commit('replaceEditRequest', data);
                trackEditRequestEdited({ editRequestUuid: uuid, intake, product: intakeProduct(rootGetters, intake) });
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                if (STALE_EDIT_REQUEST_STATUSES.includes(e.response?.status) && state.editRequestsIntake === intake) {
                    dispatch('loadEditRequests', intake);
                }
                throw e;
            }
        },
        async withdrawEditRequest({ commit, dispatch, rootGetters, state }, uuid) {
            const intake = state.editRequestsIntake;
            try {
                await axios.post(
                    editRequestsUrl(rootGetters, intake, `${uuid}/withdraw/`),
                    {},
                    rootGetters.axiosConfig,
                );
                if (state.editRequestsIntake === intake) commit('removeEditRequest', uuid);
                trackEditRequestWithdrawn({
                    editRequestUuid: uuid,
                    intake,
                    product: intakeProduct(rootGetters, intake),
                });
            } catch (e) {
                logApiError(e, dispatch);
                if (STALE_EDIT_REQUEST_STATUSES.includes(e.response?.status) && state.editRequestsIntake === intake) {
                    dispatch('loadEditRequests', intake);
                }
                throw e;
            }
        },
        async deleteConversation({ commit, dispatch, rootGetters }, id) {
            try {
                await axios.delete(conversationsUrl(rootGetters, `${id}/`), rootGetters.axiosConfig);
                commit('removeConversation', id);
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
    },
};
